import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useDatabase } from './useDatabase';

export interface UseFormHandlerOptions<T> {
  loadItem: (id: number) => Promise<T | undefined>;
  saveItem: (item: Omit<T, 'id' | 'createdAt' | 'updatedAt'>) => Promise<number>;
  updateItem: (id: number, item: Omit<T, 'id' | 'createdAt' | 'updatedAt'>) => Promise<void>;
  redirectPath: string;
  defaultValues: Omit<T, 'id' | 'createdAt' | 'updatedAt'>;
}

export function useFormHandler<T>({
  loadItem,
  saveItem,
  updateItem,
  redirectPath,
  defaultValues,
}: UseFormHandlerOptions<T>) {
  const { id } = useParams();
  const navigate = useNavigate();
  const { error, loadItem: dbLoadItem, saveItem: dbSaveItem, updateItem: dbUpdateItem } = useDatabase();
  
  const [submitting, setSubmitting] = useState(false);
  const [initialData, setInitialData] = useState<T | null>(null);
  
  const isEditing = id !== 'new';
  const itemId = isEditing ? parseInt(id!) : null;

  const loadData = useCallback(async () => {
    if (!itemId) return;
    
    // No loading state for local data - it loads instantly
    const data = await dbLoadItem(() => loadItem(itemId), 'item');
    if (data) {
      setInitialData(data);
    }
  }, [itemId, dbLoadItem, loadItem]);

  useEffect(() => {
    if (isEditing && itemId) {
      loadData();
    }
  }, [isEditing, itemId, loadData]);

  const handleSubmit = async (formData: Omit<T, 'id' | 'createdAt' | 'updatedAt'>) => {
    setSubmitting(true);
    
    try {
      if (isEditing && itemId) {
        const success = await dbUpdateItem(() => updateItem(itemId, formData), 'item');
        if (success) navigate(redirectPath);
      } else {
        const result = await dbSaveItem(() => saveItem(formData), 'item');
        if (result) navigate(redirectPath);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(redirectPath);
  };

  return {
    isEditing,
    submitting,
    error,
    initialData,
    defaultValues,
    handleSubmit,
    handleCancel,
  };
}